/**
 * WaitingScreen - Pre-Quiz Waiting Room
 * 
 * Displays while participants wait for the quiz to begin.
 * Shows participant list and quiz info.
 * 
 * Features:
 * - Participant list with join animations
 * - Quiz title and description
 * - Countdown to start (if available)
 * - Ready indicator
 * - Dark mode support
 */

import React, { useEffect, useState } from 'react';
import { useTheme } from '../../../context/ThemeContext';
import { ThemeToggle } from '../../../context/ThemeContext';

/**
 * WaitingScreen component
 * @param {string} shortCode - Quiz short code for display
 * @param {Object} settings - Quiz settings
 * @param {Array} participants - List of participants
 * @param {string} currentParticipantId - Current participant's ID
 * @param {string} currentParticipantName - Current participant's name
 * @param {number} totalQuestions - Total number of questions
 * @param {Function} onLeave - Callback when participant leaves
 */
export function WaitingScreen({
  shortCode,
  settings = {},
  participants: participantsInput = [],
  currentParticipantId,
  currentParticipantName,
  totalQuestions = 0,
  onLeave,
}) {
  const { darkMode } = useTheme();
  const [dots, setDots] = useState('');

  // Convert participants to array if it's an object (Firebase format)
  const participants = Array.isArray(participantsInput) 
    ? participantsInput 
    : Object.entries(participantsInput || {}).map(([id, data]) => ({ id, ...data }));

  // Animated dots
  useEffect(() => {
    const interval = setInterval(() => {
      setDots(prev => prev.length >= 3 ? '' : prev + '.');
    }, 500);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className={`min-h-screen flex flex-col ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      {/* Header */}
      <header
        className={`
          py-4 px-6 flex items-center justify-between
          ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}
          border-b
        `}
      >
        <div className="flex items-center gap-4">
          <h1 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            Warteraum
          </h1>
          {shortCode && (
            <span
              className={`
                px-3 py-1 rounded-full text-sm font-mono font-bold
                ${darkMode ? 'bg-indigo-900/30 text-indigo-400' : 'bg-indigo-100 text-indigo-700'}
              `}
            >
              {shortCode}
            </span>
          )}
        </div>
        <div className="flex items-center gap-4">
          <ThemeToggle />
          {onLeave && (
            <button
              onClick={onLeave}
              className={`
                px-3 py-1.5 rounded-lg text-sm
                ${darkMode 
                  ? 'text-red-400 hover:bg-red-900/30' 
                  : 'text-red-600 hover:bg-red-50'}
                theme-transition
              `}
            >
              Verlassen
            </button>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex flex-col items-center justify-center p-6">
        {/* Waiting Animation */}
        <div className="text-center mb-8">
          <div
            className={`
              inline-flex items-center justify-center w-24 h-24 rounded-full
              ${darkMode ? 'bg-indigo-900/30' : 'bg-indigo-100'}
              mb-6 animate-pulse
            `}
          >
            <svg
              className={`w-12 h-12 ${darkMode ? 'text-indigo-400' : 'text-indigo-600'}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>

          <h2 className={`text-2xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            Warte auf Start{dots}
          </h2>
          
          <p className={`${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Das Quiz beginnt gleich!
          </p>
        </div>

        {/* Quiz Info Card */}
        <div
          className={`
            w-full max-w-md p-6 rounded-2xl shadow-lg mb-8
            ${darkMode ? 'bg-gray-800' : 'bg-white'}
          `}
        >
          {/* Quiz Title */}
          {settings.quizName && (
            <div className="text-center mb-4">
              <h3 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {settings.quizName}
              </h3>
              {settings.quizDescription && (
                <p className={`mt-1 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {settings.quizDescription}
                </p>
              )}
            </div>
          )}

          {/* Quiz Stats */}
          <div className="grid grid-cols-2 gap-4">
            {/* Total Questions */}
            <div
              className={`
                p-4 rounded-xl text-center
                ${darkMode ? 'bg-gray-700/50' : 'bg-gray-50'}
              `}
            >
              <p className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {totalQuestions}
              </p>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Fragen
              </p>
            </div>

            {/* Participants */}
            <div
              className={`
                p-4 rounded-xl text-center
                ${darkMode ? 'bg-gray-700/50' : 'bg-gray-50'}
              `}
            >
              <p className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {participants.length}
              </p>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Teilnehmer
              </p>
            </div>
          </div>

          {/* Settings Info */}
          <div className="mt-4 flex flex-wrap gap-2 justify-center">
            {settings.enableGamification && (
              <span
                className={`
                  px-2 py-1 rounded-full text-xs
                  ${darkMode ? 'bg-yellow-900/30 text-yellow-400' : 'bg-yellow-100 text-yellow-700'}
                `}
              >
                Punkte
              </span>
            )}
            {settings.questionTimerSeconds && (
              <span
                className={`
                  px-2 py-1 rounded-full text-xs
                  ${darkMode ? 'bg-blue-900/30 text-blue-400' : 'bg-blue-100 text-blue-700'}
                `}
              >
                ⏱️ {settings.questionTimerSeconds}s Timer
              </span>
            )}
            {settings.showLeaderboard && (
              <span
                className={`
                  px-2 py-1 rounded-full text-xs
                  ${darkMode ? 'bg-purple-900/30 text-purple-400' : 'bg-purple-100 text-purple-700'}
                `}
              >
                Rangliste
              </span>
            )}
          </div>
        </div>

        {/* Participants List */}
        <div
          className={`
            w-full max-w-md p-6 rounded-2xl shadow-lg
            ${darkMode ? 'bg-gray-800' : 'bg-white'}
          `}
        >
          <h3 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            Teilnehmer ({participants.length})
          </h3>

          <div className="flex flex-wrap gap-2">
            {participants.map((participant, index) => {
              const isCurrentUser = participant.id === currentParticipantId;
              
              return (
                <span
                  key={participant.id || index}
                  className={`
                    px-3 py-1.5 rounded-full text-sm font-medium
                    ${isCurrentUser
                      ? darkMode
                        ? 'bg-indigo-600 text-white'
                        : 'bg-indigo-500 text-white'
                      : darkMode
                        ? 'bg-gray-700 text-gray-300'
                        : 'bg-gray-100 text-gray-700'
                    }
                    ${isCurrentUser ? 'ring-2 ring-offset-2 ring-indigo-500' : ''}
                    transition-all duration-300 animate-fadeIn
                  `}
                  style={{
                    animationDelay: `${index * 50}ms`
                  }}
                >
                  {participant.name || `Teilnehmer ${index + 1}`}
                </span>
              );
            })}
          </div>

          {participants.length === 0 && (
            <p className={`text-center ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              Noch keine Teilnehmer...
            </p>
          )}
        </div>

        {/* Current User Status */}
        <div className={`mt-6 text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          <p className="flex items-center justify-center gap-2">
            <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
            Du bist als <strong className={darkMode ? 'text-white' : 'text-gray-900'}>
              {currentParticipantName}
            </strong> angemeldet
          </p>
        </div>
      </main>

      {/* Footer */}
      <footer className={`py-4 text-center text-sm ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
        Warte auf den Quiz-Leiter...
      </footer>

      {/* CSS for fade-in animation */}
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out forwards;
        }
      `}</style>
    </div>
  );
}

export default WaitingScreen;
